import { IFilterColumn } from 'components/report-content/components/manage-filters/index';
import Moment from 'moment/moment';
import {
    ComponentSettingsManageFilters,
    ManageFiltersFilterValueRow,
    ManageFiltersInitialDataResponse,
    RendererFilter,
    SOFT_GROUPING_PARENT_MAP_KEY,
} from '@report-content';
import { resolveParentFilter } from './getAvailableParentFilters';

// Narrows a child filter's value rows down to those valid for the parent's currently selected value(s).
// No parent, or parent with no selection ("all"), means no narrowing.
function narrowByParent(
    rows: Array<ManageFiltersFilterValueRow> | null,
    parentField?: RendererFilter | null,
): Array<ManageFiltersFilterValueRow> | null {
    if (!rows || !parentField) {
        return rows;
    }
    const selectedParentValues = (parentField.savedValues ?? []).map(String);
    if (!selectedParentValues.length) {
        return rows;
    }
    return rows.filter((row) => {
        const parentMap = row[SOFT_GROUPING_PARENT_MAP_KEY];
        return Array.isArray(parentMap) && parentMap.some((value) => selectedParentValues.includes(String(value)));
    });
}

function parseDate(value: string): Date {
    //@ts-ignore
    const match: Array<number> | undefined = value.match(/(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/);
    if (!match) {
        return new Date();
    }

    match.shift(); // discard the "full match" index
    match[1]--;
    return new Date(match[0], match[1], match[2], match[3], match[4], match[5]);
}

function getStdFormatDate(date: Date) {
    return Moment(date).format('yyyy-MM-DD');
}

function getStdFormatDateTime(date: Date, withTime: boolean = false, withSec: boolean = false) {
    let dateString = getStdFormatDate(date);
    if (withTime) {
        dateString += ' ' + getStdFormatTime(date, withSec);
    }
    return dateString;
}

function padLeft(number: number, base: number = 10, chr: string = '0') {
    let len = String(base).length - String(number).length + 1;
    return len > 0 ? new Array(len).join(chr) + number : number;
}

function getStdFormatTime(time: Date, withSec: boolean = false) {
    let hh = padLeft(time.getHours()),
        ii = padLeft(time.getMinutes()),
        ss = padLeft(time.getSeconds()),
        timeString = [hh, ii].join(':');
    if (withSec) {
        timeString += ':' + ss;
    }
    return timeString;
}

export function buildFilterColumn(
    field: RendererFilter,
    initialData?: ManageFiltersInitialDataResponse,
    parentField?: RendererFilter | null,
) {
    // this.settings.fields[i].canBeCached = false;
    const fc: IFilterColumn = {
        uid: field.reference_name_escaped,
        dataset_column_id: field.dataset_column_id,
        reference_name: field.reference_name,
        filterType: field.filterType ?? 'dropdown',
        column_name: field.filter_label?.length ? field.filter_label : field.column_name,
        show_date_only: 'undefined' !== typeof field.show_date_only && 'Y' === field.show_date_only ? 'Y' : 'N',
        include_all_value: field.include_all_value ?? 'Y',
        value_type: field.value_type,
        column_sequence: field.column_sequence,
        use_external_filter_value: field?.use_external_filter_value,
        external_delimiter: field?.external_delimiter,
        external_dataset_column_value_id: field?.external_dataset_column_value_id,
        external_dataset_column_title_id: field?.external_dataset_column_title_id,
        savedValues: typeof field.savedValues !== 'undefined' ? field.savedValues : [],
        data: '',
        chartingIntervals: [],
        sortDirection: field.sortDirection ?? 'ASC',
        set_default_value: field?.set_default_value ?? 'N',
        filter_default_value: field?.filter_default_value ?? [],
        override_all_value: field?.override_all_value ?? 'N',
        override_all_value_label: field?.override_all_value_label,
        parent_reference_name: field.parent_reference_name ?? null,
    };

    if (initialData) {
        if (fc.filterType == 'dropdown') {
            if (fc.savedValues?.length > 1) {
                fc.savedValues = [fc.savedValues[0]];
            }
        }

        if (field.filterType == 'range' || field.filterType == 'charting_interval') {
            fc.data = {};

            const from = field.reference_name + '-from';
            const to = field.reference_name + '-to';

            if (typeof initialData.dataResults?.[from]?.[0]?.[field.reference_name] != 'undefined') {
                fc.data.from = initialData.dataResults[from][0][field.reference_name];
            }

            if (typeof initialData.dataResults?.[to]?.[0]?.[field.reference_name] != 'undefined') {
                fc.data.to = initialData.dataResults[field.reference_name + '-to'][0][field.reference_name];
            }

            if (field.filterType == 'range' && field.value_type == 'datetime' && field.show_date_only == 'Y') {
                if (fc.data?.from > '') {
                    try {
                        fc.data.from = fc.data.from.split(' ')[0];
                    } catch (e) {}
                }
                if (fc.data?.to > '') {
                    try {
                        fc.data.to = fc.data.to.split(' ')[0];
                    } catch (e) {}
                }
            }

            if (fc.savedValues.length == 0) {
                if (field.filterType == 'range') {
                    fc.savedValues[0] = fc.data.from ?? '';
                    fc.savedValues[1] = fc.data.to ?? '';
                } else {
                    fc.savedValues = [''];
                }
            }

            if (field.filterType == 'charting_interval') {
                fc.chartingIntervals = [{ label: 'All', value: '' }];
                if (fc.data.from) {
                    const steps = [
                        { label: '2 Months', v: 2 },
                        { label: '6 Months', v: 6 },
                        { label: '1 Year', v: 12 },
                        { label: '2 Years', v: 24 },
                        { label: '5 Years', v: 60 },
                        { label: '10 Years', v: 120 },
                    ];
                    let dt = parseDate(fc.data.to);
                    let dt2 = getStdFormatDateTime(new Date(dt.setDate(dt.getDate() - 28)), true, true);
                    fc.chartingIntervals.push({ label: '4 Weeks', value: dt2 });

                    if (dt2 > fc.data.from) {
                        for (let j = 0; j < steps.length; j++) {
                            dt = parseDate(fc.data.to);
                            dt2 = getStdFormatDateTime(new Date(dt.setMonth(dt.getMonth() - steps[j].v)), true, true);

                            fc.chartingIntervals.push({ label: steps[j].label, value: dt2 });
                            if (dt2 < fc.data.from) {
                                break;
                            }
                        }
                    }
                }
            }
        } else {
            fc.data = narrowByParent(initialData?.dataResults?.[field.reference_name] ?? null, parentField);
        }
    }

    return fc;
}

export default function buildFilterColumns(
    settings: ComponentSettingsManageFilters,
    initialData?: ManageFiltersInitialDataResponse,
) {
    if (!settings.fields) {
        return [];
    }
    return settings.fields
        .filter((field) => field.filter_column == 'Y')
        .map((field) =>
            buildFilterColumn(
                field,
                initialData,
                resolveParentFilter(field, settings.fields, settings.only_display_relevant_values == 'Y'),
            ),
        )
        .sort((a, b) => a.column_sequence - b.column_sequence);
}
